import { isMobileDevice } from '@/lib/mobileWalletUtils';
import { useCallback, useState, useEffect } from 'react';
import { usePrimaryWallet } from './useWalletQueries';
import { getMWAConnectionState, isMWAAvailable } from '@/lib/mwaAdapter';

/**
 * Hook for managing Mobile Wallet Adapter (MWA) connection state
 *
 * Now uses the official Wallet Standard protocol via
 * @solana-mobile/wallet-standard-mobile which properly handles:
 * - Wallet registration via registerMwa()
 * - Authorization caching for persistent sessions
 * - Android Intent-based wallet connections
 * - Wallet Standard protocol
 * - Automatic session restoration
 *
 * Key improvements from old implementation:
 * - Session persists across page reloads (via Wallet Standard authorization cache)
 * - Works with Seekr and all MWA-compliant wallets
 * - Better error handling and user feedback
 * - Official approach maintained by Solana Mobile team
 */
const useWalletMobileMWA = () => {
  const [isConnecting, setIsConnectingMWA] = useState<boolean>(() =>
    localStorage.getItem('IS_CONNECTING_MWA') === 'true'
  );
  const { data: primaryWalletData } = usePrimaryWallet();
  const primaryWallet = primaryWalletData?.wallet;

  /**
   * Set MWA connection state
   * Persists to localStorage for cross-session tracking
   */
  function setIsConnecting(isConnecting: boolean) {
    if (isConnecting) {
      setIsConnectingMWA(isConnecting);
      localStorage.setItem('IS_CONNECTING_MWA', isConnecting.toString());
    } else {
      setIsConnectingMWA(isConnecting);
      localStorage.removeItem('IS_CONNECTING_MWA');
    }
  }

  /**
   * Check if there's a valid MWA session
   *
   * For MWA using Wallet Standard, a valid session means:
   * 1. User is on mobile device
   * 2. Primary wallet adapter is MWA
   * 3. MWA wallet has an active connection (checked via Wallet Standard)
   *
   * @returns boolean indicating if valid MWA session exists
   */
  const hasValidMWASession = useCallback((): boolean => {
    try {
      // Must be on mobile device
      if (!isMobileDevice()) {
        return false;
      }

      // Must have MWA as primary wallet adapter
      if (primaryWallet?.walletAdapter?.toLowerCase() !== 'mwa') {
        return false;
      }

      // Check if Wallet Standard connection is active
      const connectionState = getMWAConnectionState();
      return connectionState.isConnected && connectionState.publicKey !== null;
    } catch {
      return false;
    }
  }, [primaryWallet?.walletAdapter]);

  /**
   * Auto-restore MWA session on mount if primary wallet is MWA
   *
   * The Wallet Standard protocol handles authorization caching automatically.
   * When registerMwa() is called with createDefaultAuthorizationCache(),
   * it stores authorization in localStorage.
   *
   * On subsequent page loads, if the user previously authorized the connection,
   * the wallet can reconnect without showing the wallet picker again.
   */
  useEffect(() => {
    const restoreMWASession = async () => {
      // Only restore if MWA is the primary wallet
      if (!primaryWallet || primaryWallet.walletAdapter?.toLowerCase() !== 'mwa') {
        return;
      }

      // Only restore on mobile devices
      if (!isMobileDevice()) {
        return;
      }

      // Check if MWA wallets are available
      if (!isMWAAvailable()) {
        console.log('[MWA] No MWA wallets detected, skipping session restore');
        return;
      }

      // Check if already connected
      const connectionState = getMWAConnectionState();
      if (connectionState.isConnected) {
        console.log('[MWA] Already connected, no need to restore');
        return;
      }

      try {
        // Note: The Wallet Standard authorization cache handles persistence
        // If the user previously authorized, the next connect() call will
        // silently reconnect without showing the wallet picker.
        //
        // We don't manually trigger connect() here because:
        // 1. It would show wallet picker if auth cache is empty
        // 2. The user should explicitly click to connect
        // 3. The WalletContext auto-reconnect logic will handle this

        console.log('[MWA] Session restore skipped - will use WalletContext auto-reconnect');
      } catch (error) {
        // Authorization expired or revoked
        console.log('[MWA] Could not restore session:', error);
      }
    };

    restoreMWASession();
  }, [primaryWallet]);

  return {
    isConnecting,
    setIsConnecting,
    hasValidMWASession,
  };
};

export default useWalletMobileMWA;
